import React, { useState, useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import get from "lodash/get";
import { Tooltip, Typography } from "antd";
import format from "date-fns/format";
import moment from "moment/min/moment-with-locales";
import { enUS, es } from "date-fns/locale";
import { IQuote, IGroupedShareCompareCondo, ISessionKey } from "@share/common-types";
import { RefundableEnum } from "@common-types";
import { IMarginatorState, ILoginState } from "@share/store/slices";
import { formatDateCheckInOut, renderNumber, isSpanish, getSelectedCurrency } from "@share/utils";
import { DATE_FORMAT_CHECK_IN_OUT } from "@constants";
import { Currency } from "@components";
import { EnvironmentOutlined, CalendarOutlined } from "@ant-design/icons";
import { ExpiredQuoteNotice } from '../expired-notice';
import { buildSearchAgainUrl } from '../search-again-url';

interface ICondoTabProps {
  condos: Record<string, IGroupedShareCompareCondo[]>;
  marginatorStore: IMarginatorState;
  loginStore: ILoginState;
  condosessionKey: ISessionKey | null | undefined;
  onCancel: () => void;
  isHistorical?: boolean;
}

const percents = 100;
const DATE_FORMAT = "d MMM";
const ONE = 1;

export const CondoTab: React.FC<ICondoTabProps> = ({
  condos,
  marginatorStore,
  loginStore,
  isHistorical,
}) => {
  const locations = Object.keys(condos);
  const [selectedLocation, setSelectedLocation] = useState(locations[0] || '');
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const { account } = loginStore;
  const currency = getSelectedCurrency(account as any);

  const currentGroups = condos[selectedLocation] || [];

  // "Search again" link for the expired notice: first condo of the currently selected city.
  const searchAgainUrl = useMemo(
    () => buildSearchAgainUrl((condos[selectedLocation]?.[0]?.items?.[0] as any)?.link, 'condo'),
    [condos, selectedLocation]
  );

  useEffect(() => {
    if (!selectedLocation && locations.length > 0) {
      setSelectedLocation(locations[0]);
    } else if (selectedLocation && !condos[selectedLocation]) {
      setSelectedLocation(locations[0] || '');
    }
  }, [condos]);

  useEffect(() => {
    if (activeGroupIndex >= currentGroups.length && currentGroups.length > 0) {
      setActiveGroupIndex(currentGroups.length - 1);
    }
  }, [currentGroups.length]);

  const locationName = (currentGroups[activeGroupIndex]?.items?.[0] as any)?.locationObj?.name || selectedLocation;
  console.log(currentGroups[activeGroupIndex],'currentGroups[activeGroupIndex]')

    const handleView = (quote: IQuote) => {
      const link = quote.link.startsWith('http') ? quote.link : `${window.location.origin}${quote.link}`;
      const [basePart, queryPart] = link.split('?');
      const queryWithoutQuote = (queryPart || '')
        .replace(/&?quote=[^&]*/g, '')
        .replace(/^&/, '');
      const linkWithoutQuote = queryWithoutQuote ? `${basePart}?${queryWithoutQuote}` : basePart;
      const sep = linkWithoutQuote.includes('?') ? '&' : '?';
      window.open(`${linkWithoutQuote}${sep}quote=${quote.userWhoShared}`, '_blank');
    };

  return (
    <div>
      {(locations.length > 1 || currentGroups.length > 1) && (
        <div className="hotels-wrapper">
          {/* LOCATION TABS */}
          {locations.length > 1 && (
            <div className="hotels-row">
              <div className="icon-box">
                <EnvironmentOutlined />
              </div>

              <div className="tabs-container locations-tabs">
                {locations.map((locKey) => (
                  <button
                    key={locKey}
                    className={`tab-btn ${selectedLocation === locKey ? "active" : ""}`}
                    onClick={() => {
                      setSelectedLocation(locKey);
                      setActiveGroupIndex(0);
                    }}
                  >
                    {condos[locKey][0]?.items[0]?.locationObj?.name || locKey}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* DATE + GUEST TABS */}
          {currentGroups.length > 1 && (
            <div className="hotels-row">
              <div className="icon-box">
                <CalendarOutlined />
              </div>

              <div className="tabs-container">
                {currentGroups.map((group, index) => {
                  let label: React.ReactNode = "";
                  if (group.searchType === "Flexible") {
                    if (group.selectedMonths?.length) {
                      const formattedMonths = group.selectedMonths.map((month) => {
                        return moment(month)
                          .locale(isSpanish() ? "es" : "en")
                          .format("MMMM");
                      });
                      const years = group.selectedMonths.map((month) =>
                        moment(month).format("YYYY")
                      );
                      const allSameYear = years.every((year) => year === years[0]);

                      if (allSameYear) {
                        label = `${formattedMonths.join(" - ")} ${years[0]}`;
                      } else {
                        label = group.selectedMonths
                          .map((month) =>
                            moment(month)
                              .locale(isSpanish() ? "es" : "en")
                              .format("MMMM YYYY")
                          )
                          .join(" - ");
                      }
                    } else {
                      label = <FormattedMessage id="anytime" />;
                    }
                  } else if (  group.startDate &&  group.endDate &&  group.startDate !== "" &&  group.endDate !== "" ) {
                    label = `${formatDateCheckInOut(
                      group.startDate,
                      DATE_FORMAT_CHECK_IN_OUT,
                      isSpanish() ? es : undefined
                    )} - ${formatDateCheckInOut(
                      group.endDate,
                      DATE_FORMAT_CHECK_IN_OUT,
                      isSpanish() ? es : undefined
                    )}`;
                  } else {
                    label = "unknown";
                  }
                  return (
                    <button
                      key={index}
                      className={`tab-btn ${activeGroupIndex === index ? "active" : ""}`}
                      onClick={() => setActiveGroupIndex(index)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className='modal-compare__hotels-location'>
        {locationName ? <div>{locationName}</div> : null}
      </div>

      <div className="condo-modal-compare__hotels condo-modal-compare__hotels--meta" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className='modal-compare__hotels_link'>
          <div className="hotels-dates">
            {currentGroups[activeGroupIndex]?.searchType === "Flexible" ? (
              <>
                <FormattedMessage id="flexible.dates" /> -{" "}
                {currentGroups[activeGroupIndex]?.selectedMonths?.length ? (
                  currentGroups[activeGroupIndex]?.selectedMonths
                  ?.map((m) => moment(m, "YYYY-MM-DD").format("MMMM"))
                  .join(" - ")
                ) : (
                  <FormattedMessage id="anytime" />
                )}
              </>
            ) : (
              <>
                <FormattedMessage id="specific.dates" /> -{" "}
                {formatDateCheckInOut(
                  currentGroups[activeGroupIndex]?.startDate as string,
                  DATE_FORMAT_CHECK_IN_OUT,
                  isSpanish() ? es : undefined
                )}{" "}
                -{" "}
                {formatDateCheckInOut(
                  currentGroups[activeGroupIndex]?.endDate as string,
                  DATE_FORMAT_CHECK_IN_OUT,
                  isSpanish() ? es : undefined
                )}
              </>
            )}
          </div>
          <div className="hotels-dates text-start" >
            {(currentGroups[activeGroupIndex] as any)?.roomsDetail && (
              <>
                <FormattedMessage id="room.detail" defaultMessage="Room Detail" />: <strong>{(currentGroups[activeGroupIndex] as any)?.roomsDetail}</strong>
              </>
            )}
          </div>
        </div>
      </div>

      {isHistorical ? <ExpiredQuoteNotice searchAgainUrl={searchAgainUrl} /> : null}

      <div className="condo-modal-compare__hotels">
        {currentGroups[activeGroupIndex]?.items.map((condo, index) => {
          const { price, availableDates } = condo;

          const value = marginatorStore.value || 0;
          const marginatorPrice = (price ? price : 0) / (1 - value / 100);

          const msave = condo.publicPrice
            ? (marginatorPrice * percents) / condo.publicPrice
            : 100;
          const savings = Math.floor(percents - msave);

          return (
            <div key={index} className="condo-modal-compare__hotel">
              <div className="condo-modal-compare__media">
                <div className="condo-images-container">
                  <div
                    className="condo-image"
                    style={{
                      backgroundImage: `url(${get(condo, "images[0].url")})`,
                    }}
                  />
                  {savings > 0 ? (
                    <div className="condo-modal-compare__savings-badge">
                      {savings.toFixed(0)} %{" "}
                      <FormattedMessage id="results.hotel.card.compare.modal.off" />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="condo-text-container condo-text-container--title">
                <Tooltip title={condo.displayName} placement="top">
                  <p>{condo.displayName}</p>
                </Tooltip>
              </div>

              <div className="condo-text-container condo-text-container--address">
                <Tooltip title={condo.fullAddress ?? ""} placement="top">
                  <p>{condo.fullAddress}</p>
                </Tooltip>
              </div>

              <div
                className="condo-text-container"
                style={{ height: isSpanish() ? "60px" : "40px" }}
              >
                {availableDates?.length ? (
                  <div className="condo-modal-compare__date-label">
                    {availableDates.length >= ONE && (
                      <div className="condo-modal-compare__date-more">
                        <p className="condo-modal-compare__date-more-text">
                          <Tooltip
                            className="condo-compare-tooltip"
                            overlayStyle={{ whiteSpace: "pre-line" }}
                            placement="top"
                            title={
                              <>
                                {availableDates.map((d, i) => (
                                  <Typography
                                    key={i}
                                    style={{ color: "#FFFFFF" }}
                                  >
                                    {format(new Date(d.from), DATE_FORMAT, {
                                      locale: isSpanish() ? es : enUS,
                                    })}{" "}
                                    -{" "}
                                    {format(new Date(d.to), DATE_FORMAT, {
                                      locale: isSpanish() ? es : enUS,
                                    })}
                                  </Typography>
                                ))}
                              </>
                            }
                          >
                            {availableDates.length}{" "}
                            <FormattedMessage id="available.dates" />
                          </Tooltip>
                        </p>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {condo.refundability &&
              condo.refundability !== RefundableEnum.Unknown ? (
                <div className="condo-text-container condo-text-container--refundable">
                  <div className="hotel-card-wrapper__refundable">
                    {condo.refundability === RefundableEnum.Refundable && (
                      <div className="hotel-card-wrapper__refundable-ref">
                        <FormattedMessage id="refundable" />
                      </div>
                    )}
                    {condo.refundability === RefundableEnum.Nonrefundable && (
                      <div className="hotel-card-wrapper__refundable-nonref">
                        <FormattedMessage id="non.refundable" />
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              <div
                className="condo-amenities-container"
                style={{ height: isSpanish() ? "60px" : "43px" }}
              >
                {condo.rating?.value || condo.rating?.reviewsCount ? (
                  <label>
                    {condo.rating?.value ? (
                      <>
                        <strong>{condo.rating?.value.toFixed(1)}</strong> / 5
                      </>
                    ) : null}{" "}
                    {condo.rating?.reviewsCount ? (
                      <>
                        ({condo.rating?.reviewsCount}{" "}
                        <FormattedMessage id="reviews" />)
                      </>
                    ) : null}
                  </label>
                ) : (
                  <label>
                    <FormattedMessage id="reviews.guest.no.ratings" />
                  </label>
                )}
              </div>

              <div
                className={`condo-text-container condo-text-container--price${isHistorical ? ' condo-text-container--flush-bottom' : ''
                  }`}
              >
                {condo.publicPrice ? (
                  <p style={{ textDecoration: "line-through" }}>
                    <Currency currency={currency} />
                    {renderNumber(condo.publicPrice, 2)}
                  </p>
                ) : null}
                <p>
                  <strong>
                    <Currency currency={currency} />
                    {renderNumber(marginatorPrice, 2)}
                  </strong>
                </p>
              </div>

              {!isHistorical ? (
                <div className="condo-view-container">
                  <div
                    className="view-button"
                    style={(condo as any).isPast ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    onClick={() => { if (!(condo as any).isPast) handleView(condo); }}
                  >
                    <FormattedMessage id="results.hotel.card.compare.modal.view" />
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

